import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { Box, Button, Typography } from '@mui/material';
import SquareFootOutlined from '@mui/icons-material/SquareFootOutlined';
import LocationService from '../../../../shared/services/location/location.service';
import { IEditLocation, ILocation, IPostDeactivateLocation } from '../../../../shared/services/location/location.model';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { DeactivateLocation } from '../../../dialogs/organisation/deactivate-location-dialog';
import { LocationEditForm } from '../../../presentation-components/organisation-components/location/location-edit-form';
import { EntityFindingsPanel } from '../../../presentation-components/readiness-components/entity-findings-panel';
import { DialogWrapper } from '../../../presentation-components/display-elements-components/dialog-wrapper';
import { FloorAreaEstimator } from '../../../presentation-components/emissons-components/overview/floor-area-estimator';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import ContactSupplierService from '../../../../shared/services/contact-supplier/contact-supplier.service';
import { IContactSupplierDetails } from '../../../../shared/services/contact-supplier/contact-supplier.model';
import { notifyError } from '../../../../shared/utils/notify-error';

export const OrganisationLocationEditPage = () => {
  const navigate = useNavigate()
  const { id } = useParams();
  //Services
  const locationService = LocationService.getInstance();
  const contactSupplierService = ContactSupplierService.getInstance();
  //Context
  const screenLabelsContext = useContext(ScreenLabelsContext)
  const userNotificationsContext = useContext(UserNotificationsContext)
  const reportPeriodContext = useContext(ReportPeriodContext)
  //State
  const [contactSuppliers, setContactSuppliers] = useState<IContactSupplierDetails[]>();
  const [location, setLocation] = useState<ILocation>();
  const [activeLocations, setActiveLocations] = useState<ILocation[]>()
  const [editLocation, setEditLocation] = useState<IEditLocation>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>('')
  const [errorCode, setErrorCode] = useState<number>()
  const [editField, setEditField] = useState<string>('')
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [onlyHasOneLocation, setOnlyHasOneLocation] = useState<boolean>(false)
  const [showEstimator, setShowEstimator] = useState<boolean>(false)
  const [estimatorSubmitting, setEstimatorSubmitting] = useState<boolean>(false)
  const estimatorSubmitRef = useRef<(() => void) | undefined>(undefined)
  //Loading State
  const [isDialogSubmitting, setIsDialogSubmitting] = useState<boolean>(false)
  const [isFormSubmitting, setIsFormSubitting] = useState<boolean>(false)
  //The report period the estimate will be booked into (the one currently selected app-wide).
  const selectedReportPeriod = reportPeriodContext.allReportPeriods.find(
    period => period.id === reportPeriodContext.lastSelectedReportId
  )
  //Use Effects
  useEffect(() => {
    if (id) {
      getLocation(id)
    }
    locationService.getActiveLocations()
      .then(response => {
        setActiveLocations(response)
        setOnlyHasOneLocation(response.length === 1)
      })
      .catch(err => console.log(err))

    getContactSuppliers()
  }, [])

  const getContactSuppliers = () => {
    // The location form only needs supplier id + name for its energy-supplier pickers, so use the
    // lightweight endpoint. The full list (with spend-based conversions) can be multiple MB for
    // suppliers-heavy orgs and was blocking the whole form from rendering.
    contactSupplierService.getActiveSuppliersLite(false)
      .then(response => {
        setContactSuppliers(response)
      })
      .catch(err => {
        console.log(err)
      })
  }

  const getLocation = (id: string) => {
    locationService.getOneLocation(id)
      .then(response => {
        setLocation(response);
        setEditLocation({
          id: response.id,
          friendlyName: response.friendlyName,
          addressLineOne: response.addressLineOne,
          addressLineTwo: response.addressLineTwo,
          city: response.city,
          postcode: response.postcode,
          countryId: response.countryId,
          startDate: response.startDate,
          endDate: response.endDate,
          numberOfEmployees: response.numberOfEmployees,
          buildingTypeId: response.buildingTypeId,
          hasCooling: response.hasCooling,
          hasGas: response.hasGas,
          hasSteam: response.hasSteam,
          hasAirCon: response.hasAirCon,
          hasElectricity: response.hasElectricity,
          gasBillingPeriod: response.gasBillingPeriod,
          coolingBillingPeriod: response.coolingBillingPeriod,
          heatAndSteamBillingPeriod: response.heatAndSteamBillingPeriod,
          airConBillingPeriod: response.airConBillingPeriod,
          electricityBillingPeriod: response.electricityBillingPeriod,
          locationMarketBasedConversions: response.locationMarketBasedConversions,
          isPrimaryLocation: response.isPrimaryLocation,
          ownership: response.ownership,
          hasOperationalControl: response.hasOperationalControl,
          floorSpaceOrganisation: response.floorSpaceOrganisation,
          assignedUserId: response.assignedUserId,
          floorSpaceBuilding: response.floorSpaceBuilding,
          occupancyRate: response.occupancyRate,
          locationReference: response.locationReference,
          customMarketBasedMultiplier: response.locationMarketBasedConversions && response.locationMarketBasedConversions.find(c => c.isActive) ? response.locationMarketBasedConversions.find(c => c.isActive)?.customMarketBasedMultiplier : undefined,
          electricitySupplierId: response.electricitySupplierId,
          gasSupplierId: response.gasSupplierId,
          airConSupplierId: response.airConSupplierId,
          coolingSupplierId: response.coolingSupplierId,
          heatAndSteamSupplierId: response.heatAndSteamSupplierId,
          energySupplierTariffId: response.energySupplierTariffId,
          customFieldValues: response.customFieldValues,
          subsidiaryId: response.subsidiaryId,
        })
      })
      .catch(err => {
        console.log(err)
      })
  }

  const submitHandler = async (editLocationForm: IEditLocation) => {
    if (id && editLocation) {
      setErrorMessage('')
      locationService.update(id, editLocationForm)
        .then(response => {
          getLocation(response.id!);
          refreshLocations();
          setEditField('');
          getContactSuppliers()
        })
        .catch(err => {
          if (err.response?.data?.message) {
            setErrorMessage(err.response.data.message)
            setErrorCode(err.response.data.code)
          }
          notifyError(userNotificationsContext.openSnackbar, err, 'saving the location')
        })
    }
  }

  function refreshLocations(): void {
    locationService.getAllLocations(true)
      .then(() => {
        userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
      })
      .catch(err => {
        console.log(err)
      })
  }

  const deleteHandler = (id: string, isInUse: boolean) => {
    if (isInUse) {
      userNotificationsContext.openDialog({
        active: true,
        title: 'Action Required',
        message: `You are unable to delete this location as it has or currently is in use. Please review and update any associated emissions before attempting to delete it.`,
        buttonCopy: 'Ok',
        hideCancelButton: true
      })
    } else {
      userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: `Are you sure you want to delete this location? This action cannot be undone.`, buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
        .then(response => {
          if (response) {
            if (!isFormSubmitting) {
              setIsFormSubitting(true)
              locationService.remove(id)
                .then(() => {
                  userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' });
                  navigate('/organisation/locations')
                  setIsFormSubitting(false)
                  refreshLocations()
                })
                .catch(err => {
                  if (err.response?.data?.code === 1) {
                    userNotificationsContext.openDialog({
                      active: true, title: 'Action Not Allowed',
                      message: `This ${screenLabelsContext.getLabel('location', { plural: false })} cannot be deleted because it is currently in use, or has been used previously.`,
                      buttonCopy: 'Ok',
                      hideCancelButton: true
                    })
                    console.error(err)
                  } else {
                    notifyError(userNotificationsContext.openSnackbar, err, 'deleting the location')
                  }
                  setIsFormSubitting(false)
                })
            }
          }
        })
    }
  }

  const submitDeactivateDialogHandler = (values: IPostDeactivateLocation) => {
    if (!isDialogSubmitting) {
      setErrorMessage('')
      setIsDialogSubmitting(true)
      locationService.deactivate(values.locationId, values)
        .then(response => {
          setIsDialogSubmitting(false)
          setOpenDialog(false)
          userNotificationsContext.openSnackbar({ open: true, message: 'Deactivated successfully' });
          getLocation(values.locationId)
        })
        .catch(err => {
          setIsDialogSubmitting(false)
          if (err.response?.data?.code === 1) {
            setErrorMessage(err.response.data.message)
          }
          notifyError(userNotificationsContext.openSnackbar, err, 'deactivating the location')
        })
    } else {
      userNotificationsContext.openSnackbar({ open: true, message: 'Saving in progress, please wait' });
    }
  }

  const deactivateHandler = (id: string) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: `By deactivating this ${screenLabelsContext.getLabel('location', { plural: false })}, you will not be able to add any more emissions to the ${screenLabelsContext.getLabel('location', { plural: false })}. This action cannot be undone.`,
      buttonCopy: 'Continue', cancelButtonCopy: 'Cancel'
    })
      .then(response => {
        if (response) {
          if (!isFormSubmitting) {
            setIsFormSubitting(true)
            locationService.deactivate(id)
              .then(response => {
                getLocation(id)
                userNotificationsContext.openSnackbar({ open: true, message: 'Deactivated successfully' });
                setIsFormSubitting(false)
              })
              .catch(err => {
                notifyError(userNotificationsContext.openSnackbar, err, 'deactivating the location')
                setIsFormSubitting(false)
              })
          }
        }
      })
  }

  return (
    <>
      {location &&
        <PageOuterContainer>
          <PageHeading
            title={location.friendlyName}
            includeBackButton={true}
            backButtonLink='/organisation/locations'
            backButtonText={`Back to ${screenLabelsContext.getLabel('location', { plural: true })}`}
            hasTabs={true}
            includeActionButton={true}
            actionButtonIcon={IconName.HideSourceOutlined}
            actionButtonHandler={location.isPrimaryLocation ? () => setOpenDialog(true) : () => deactivateHandler(location.id)}
            actionButtonText='Deactivate'
            hideShowActionButton={!location.active || (activeLocations && activeLocations.length === 0) || onlyHasOneLocation}
            includeSecondaryActionButton={true}
            secondaryActionButtonIcon={IconName.DeleteOutline}
            secondaryActionButtonHandler={() => deleteHandler(location.id, location.locationInUse)}
            secondaryActionButtonText='Delete'
            hideShowSecondaryActionButton={(activeLocations && activeLocations.length === 0) || onlyHasOneLocation}
            extraActions={location.active ? (
              <Button
                variant='outlined'
                startIcon={<SquareFootOutlined fontSize='small' />}
                onClick={() => {
                  if (!selectedReportPeriod) {
                    userNotificationsContext.openSnackbar({ open: true, message: 'Select a report period before estimating utilities', backgroundColor: '#70787A' })
                    return
                  }
                  setShowEstimator(true)
                }}
              >
                <Typography>Estimate utilities from floor area</Typography>
              </Button>
            ) : undefined}
          />
          <EntityFindingsPanel entityType='location' entityId={location.id} />
          {editLocation && contactSuppliers &&
            <LocationEditForm
              submitHandler={submitHandler}
              newLocation={editLocation}
              errorMessage={errorMessage}
              contactSuppliers={contactSuppliers}
              setErrorMessage={setErrorMessage}
              setErrorCode={setErrorCode}
              editField={editField}
              errorCode={errorCode}
              setEditField={setEditField}
              locationActive={location.active}
            />
          }
          {openDialog && editLocation &&
            <DeactivateLocation
              isSubmitting={isDialogSubmitting}
              open={openDialog}
              location={editLocation}
              closeHandler={() => setOpenDialog(false)}
              submitHandler={submitDeactivateDialogHandler}
              errorMessage={errorMessage}
            />}
          {showEstimator && selectedReportPeriod &&
            <DialogWrapper
              open={showEstimator}
              onClose={() => setShowEstimator(false)}
              title='Estimate utilities from floor area'
              cancelHandler={() => setShowEstimator(false)}
              submitHandler={() => estimatorSubmitRef.current?.()}
              isSubmitting={estimatorSubmitting}
              maxWidth='md'
              submitButtonText='Save estimate'
              submitButtonLoadingText='Saving'
            >
              <Box>
                <Typography variant='body2' sx={{ color: 'text.secondary', pb: 2 }}>
                  Estimates this location's utility emissions from its floor area and books them to Scope 3 Leased Assets in {selectedReportPeriod.friendlyName}. Intended for leased sites where you do not hold the utility bills.
                </Typography>
                <FloorAreaEstimator
                  location={location}
                  reportPeriodId={selectedReportPeriod.id}
                  startDate={moment(selectedReportPeriod.startDate).format('YYYY-MM-DD')}
                  endDate={moment(selectedReportPeriod.endDate).format('YYYY-MM-DD')}
                  hideActions
                  submitRef={estimatorSubmitRef}
                  onSubmittingChange={setEstimatorSubmitting}
                  onSuccess={() => {
                    setShowEstimator(false)
                    userNotificationsContext.openSnackbar({ open: true, message: 'Utilities estimated successfully' })
                    // Stay on the location and show its Emissions tab. An estimate can span scopes
                    // (gas Scope 1, electricity Scope 2, or both Scope 3 when leased), so a single
                    // emission is not the right destination; the location's own emissions list is.
                    getLocation(location.id)
                    navigate(`/organisation/locations/${location.id}?tabId=4`)
                  }}
                />
              </Box>
            </DialogWrapper>}
        </PageOuterContainer>}
    </>
  )
}